import React, { Component } from "react";
import SlotControlService from "../../services/slotcontrolservice";

// Slot Control (Audit -> Slot Control): store managers switch today's (or any date's) PICKUP slots
// off/on based on load. Blocking is PICKUP-ONLY (enforced in the pickup-booking proc); deliveries
// are unaffected. Each slot shows its live pickup load.
function localDay() {
  const d = new Date();
  const p = (n) => (n < 10 ? "0" + n : "" + n);
  return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
}

export default class SlotControl extends Component {
  constructor(props) {
    super(props);
    this.svc = new SlotControlService();
    this.state = {
      stores: [], storeId: "", date: localDay(),
      slots: [], loading: false, saving: 0, error: "", msg: "", capDraft: {},
    };
  }

  componentDidMount() {
    this.loadStores();
  }

  loadStores = async () => {
    const res = await this.svc.getStores();
    const list = (res && res.data) || (Array.isArray(res) ? res : []);
    const stores = (list || []).map((s) => ({
      id: s.StoreID != null ? s.StoreID : (s.Value != null ? s.Value : (s.value != null ? s.value : s.id)),
      name: s.StoreName || s.ShortName || s.StoreShortName || s.Text || s.text || s.label || s.name || ("Store " + (s.StoreID || s.value || "")),
    })).filter((s) => s.id != null);
    this.setState({ stores }, () => {
      if (stores.length) this.setState({ storeId: String(stores[0].id) }, this.loadSlots);
    });
  };

  loadSlots = async () => {
    const { storeId, date } = this.state;
    if (!storeId || !date) return;
    this.setState({ loading: true, error: "", msg: "" });
    const res = await this.svc.getBlackout(storeId, date);
    if (res && res.statusCode === 401) {
      this.setState({ loading: false, error: "Session expired — please log in again." });
      return;
    }
    const raw = (res && res.data) || [];
    // API serializes VMSlotBlackout as camelCase (slotID/slotName/pickupLoad/isBlocked);
    // normalize to the PascalCase fields the rest of this component uses.
    const slots = (Array.isArray(raw) ? raw : []).map((s) => ({
      SlotID: s.SlotID != null ? s.SlotID : s.slotID,
      SlotName: s.SlotName || s.slotName || s.SlotFrom || s.slotFrom || ("Slot " + (s.SlotID != null ? s.SlotID : (s.slotID != null ? s.slotID : ""))),
      PickupLoad: s.PickupLoad != null ? s.PickupLoad : (s.pickupLoad != null ? s.pickupLoad : 0),
      IsBlocked: (s.IsBlocked != null ? s.IsBlocked : s.isBlocked) ? true : false,
      Capacity: s.Capacity != null ? s.Capacity : (s.capacity != null ? s.capacity : null),
      ExecCount: s.ExecCount != null ? s.ExecCount : (s.execCount != null ? s.execCount : 0),
    })).filter((s) => s.SlotID != null);
    this.setState({ slots, loading: false });
  };

  onStore = (e) => this.setState({ storeId: e.target.value }, this.loadSlots);
  onDate = (e) => this.setState({ date: e.target.value }, this.loadSlots);

  toggle = async (slot) => {
    const { storeId, date } = this.state;
    const block = !slot.IsBlocked;
    this.setState({ saving: slot.SlotID, msg: "", error: "" });
    const res = await this.svc.setBlackout(storeId, date, slot.SlotID, block);
    if (res && res.statusCode === 401) {
      this.setState({ saving: 0, error: "Session expired — please log in again." });
      return;
    }
    const ok = res && (res.statusCode === 200 || res.result === "Success" || res.data === "SUCCESS");
    if (ok) {
      this.setState((st) => ({
        saving: 0,
        msg: (block ? "Blocked" : "Re-opened") + " " + slot.SlotName,
        slots: st.slots.map((s) => (s.SlotID === slot.SlotID ? { ...s, IsBlocked: block } : s)),
      }));
    } else {
      this.setState({ saving: 0, error: "Couldn't update the slot. Please retry." });
    }
  };

  onCapInput = (slotId, val) => {
    this.setState((st) => ({ capDraft: { ...st.capDraft, [slotId]: val } }));
  };

  saveCap = async (slot) => {
    const { storeId, capDraft } = this.state;
    const draft = capDraft[slot.SlotID];
    // nothing typed for this slot, or unchanged from current → skip
    const cur = slot.Capacity == null ? "" : String(slot.Capacity);
    if (draft === undefined || String(draft) === cur) return;
    const n = parseInt(draft, 10);
    const capacity = isNaN(n) || n <= 0 ? 0 : n; // 0/blank clears the limit
    this.setState({ saving: "cap" + slot.SlotID, msg: "", error: "" });
    const res = await this.svc.setCapacity(storeId, slot.SlotID, capacity);
    if (res && res.statusCode === 401) {
      this.setState({ saving: 0, error: "Session expired — please log in again." });
      return;
    }
    const ok = res && (res.statusCode === 200 || res.result === "Success" || res.data === "SUCCESS");
    if (ok) {
      this.setState((st) => {
        const cd = { ...st.capDraft }; delete cd[slot.SlotID];
        return {
          saving: 0,
          capDraft: cd,
          msg: capacity > 0 ? ("Limit set to " + capacity + " per executive for " + slot.SlotName) : ("Limit cleared for " + slot.SlotName),
          slots: st.slots.map((s) => (s.SlotID === slot.SlotID ? { ...s, Capacity: capacity > 0 ? capacity : null } : s)),
        };
      });
    } else {
      this.setState({ saving: 0, error: "Couldn't save the limit. Please retry." });
    }
  };

  render() {
    const { stores, storeId, date, slots, loading, saving, error, msg, capDraft } = this.state;
    const execCount = slots.length ? (slots[0].ExecCount || 0) : 0;
    const effCap = (s) => (s.Capacity != null && s.Capacity > 0 && execCount > 0) ? s.Capacity * execCount : 0;
    const isFull = (s) => effCap(s) > 0 && (s.PickupLoad || 0) >= effCap(s);
    const isToday = date === localDay();
    const maxLoad = Math.max(1, ...slots.map((s) => s.PickupLoad || 0));
    return (
      <div className="sc-root">
        <style>{CSS}</style>
        <div className="sc-head">
          <span className="sc-dot" />
          <div>
            <div className="sc-title">Slot Control</div>
            <div className="sc-sub">Switch pickup slots off/on by store &amp; date when a store is overloaded · pickup-only (deliveries unaffected)</div>
          </div>
        </div>

        <div className="sc-ctrls">
          <label className="sc-fld">Store
            <select className="sc-in" value={storeId} onChange={this.onStore}>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="sc-fld">Date
            <input type="date" className="sc-in" value={date} min={localDay()} onChange={this.onDate} />
          </label>
          <button className="sc-refresh" onClick={this.loadSlots} disabled={loading}>{loading ? "Loading…" : "↻ Refresh"}</button>
          {isToday ? <span className="sc-today">Today</span> : null}
        </div>

        {slots.length ? <div className="sc-execnote">This store has <b>{execCount}</b> active pickup executive{execCount === 1 ? "" : "s"} · each slot auto-blocks when its load reaches <b>limit × {execCount}</b></div> : null}

        {error ? <div className="sc-err">{error}</div> : null}
        {msg ? <div className="sc-msg">{msg}</div> : null}

        <div className="sc-list">
          <table className="sc-tbl">
            <thead><tr><th>Pickup slot</th><th>Load (pickups)</th><th className="sc-r">Limit / exec</th><th className="sc-r">Status</th><th className="sc-r">Action</th></tr></thead>
            <tbody>
              {slots.map((s) => (
                <tr key={s.SlotID} className={s.IsBlocked ? "sc-blocked" : (isFull(s) ? "sc-full" : "")}>
                  <td className="sc-slot">{s.SlotName}</td>
                  <td>
                    <div className="sc-loadwrap">
                      <div className={"sc-loadbar" + (isFull(s) ? " sc-barfull" : "")}><i style={{ width: Math.round(((s.PickupLoad || 0) / (effCap(s) > 0 ? effCap(s) : maxLoad)) * 100) + "%" }} /></div>
                      <span className="sc-loadn">{s.PickupLoad || 0}{effCap(s) > 0 ? " / " + effCap(s) : ""}</span>
                    </div>
                  </td>
                  <td className="sc-r">
                    <input
                      type="number" min="0" className="sc-cap"
                      placeholder="—"
                      value={capDraft[s.SlotID] != null ? capDraft[s.SlotID] : (s.Capacity != null ? s.Capacity : "")}
                      onChange={(e) => this.onCapInput(s.SlotID, e.target.value)}
                      onBlur={() => this.saveCap(s)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.saveCap(s); } }}
                      disabled={saving === ("cap" + s.SlotID)}
                      title="Max pickups PER EXECUTIVE for this slot. Effective slot cap = this × number of executives. Blank/0 = no limit."
                    />
                  </td>
                  <td className="sc-r">
                    {s.IsBlocked ? <span className="sc-pill sc-pblocked">Blocked</span>
                      : (isFull(s) ? <span className="sc-pill sc-pfull">Full</span>
                      : <span className="sc-pill sc-popen">Open</span>)}
                  </td>
                  <td className="sc-r">
                    <button
                      className={"sc-btn " + (s.IsBlocked ? "sc-reopen" : "sc-block")}
                      disabled={saving === s.SlotID}
                      onClick={() => this.toggle(s)}>
                      {saving === s.SlotID ? "…" : (s.IsBlocked ? "Re-open" : "Block")}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && slots.length === 0 ? <tr><td colSpan="5" className="sc-empty">No pickup slots configured for this store on this day.</td></tr> : null}
            </tbody>
          </table>
        </div>
        <div className="sc-foot">Blocking a slot stops new customer pickups into it for the selected date only (across app, web &amp; WhatsApp). Existing pickups and all deliveries are unaffected. Load = pickups already booked in that slot. <b>Limit / exec</b> = max pickups per executive for that slot every week; the slot's effective capacity is that × the number of active pickup executives, so it scales as staffing changes. Once Load reaches it the slot auto-blocks (shows <b>Full</b>); blank or 0 means no limit.</div>
      </div>
    );
  }
}

const CSS = `
.sc-root{--pink:#fe1d7f;--pinkink:#CF1C78;--ink:#241326;--muted:#8a7f90;--line:#ece6ee;--card:#fff;--soft:#faf7fb;--open:#1f9d55;--openbg:#e8f6ee;--block:#d92d20;--blockbg:#fdeceb;font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:var(--ink);padding:18px 20px;max-width:900px}
.sc-head{display:flex;align-items:center;gap:12px;margin-bottom:6px}
.sc-dot{width:11px;height:11px;border-radius:50%;background:var(--pink);box-shadow:0 0 0 4px rgba(254,29,127,.18)}
.sc-title{font-size:18px;font-weight:800}
.sc-sub{font-size:12.5px;color:var(--muted)}
.sc-ctrls{display:flex;gap:14px;align-items:flex-end;flex-wrap:wrap;margin:16px 0 10px}
.sc-fld{display:flex;flex-direction:column;gap:4px;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.03em}
.sc-in{border:1px solid var(--line);background:var(--card);border-radius:9px;padding:8px 11px;font:inherit;font-size:14px;color:var(--ink);min-width:190px}
.sc-refresh{border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:9px;padding:9px 14px;font:inherit;font-weight:600;cursor:pointer}
.sc-today{font-size:11px;font-weight:800;color:var(--pinkink);background:rgba(254,29,127,.12);border-radius:999px;padding:4px 11px}
.sc-err{background:var(--blockbg);border:1px solid #f5c6c0;color:var(--block);border-radius:10px;padding:10px 14px;margin:8px 0;font-weight:600;font-size:13px}
.sc-msg{background:var(--openbg);border:1px solid #bfe6cd;color:var(--open);border-radius:10px;padding:10px 14px;margin:8px 0;font-weight:600;font-size:13px}
.sc-list{background:var(--card);border:1px solid var(--line);border-radius:16px;box-shadow:0 1px 2px rgba(36,19,38,.04),0 6px 18px rgba(36,19,38,.05);overflow:hidden;margin-top:6px}
.sc-tbl{width:100%;border-collapse:collapse;font-size:14px}
.sc-tbl th{text-align:left;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:var(--muted);font-weight:700;padding:12px 16px;background:var(--soft);border-bottom:1px solid var(--line)}
.sc-tbl td{padding:13px 16px;border-bottom:1px solid var(--line)}
.sc-tbl tr:last-child td{border-bottom:0}
.sc-r{text-align:right}
.sc-slot{font-weight:700}
.sc-blocked{background:rgba(217,45,32,.035)}
.sc-loadwrap{display:flex;align-items:center;gap:9px;max-width:260px}
.sc-loadbar{flex:1;height:9px;border-radius:5px;background:#efe7ee;overflow:hidden}
.sc-loadbar i{display:block;height:100%;background:linear-gradient(90deg,#f6b24a,#e8590c)}
.sc-loadn{font-variant-numeric:tabular-nums;font-weight:700;min-width:22px;text-align:right}
.sc-barfull i{background:linear-gradient(90deg,#e8590c,#c92a2a)}
.sc-execnote{font-size:12.5px;color:var(--muted);margin:2px 0 12px;padding:8px 12px;background:var(--openbg);border-radius:9px}
.sc-cap{width:62px;padding:6px 8px;border:1px solid #d9d2d8;border-radius:8px;font:inherit;text-align:center;font-variant-numeric:tabular-nums}
.sc-cap:focus{outline:none;border-color:var(--block)}
.sc-pill{display:inline-block;padding:3px 11px;border-radius:999px;font-size:11.5px;font-weight:800}
.sc-popen{color:var(--open);background:var(--openbg)}
.sc-pblocked{color:var(--block);background:var(--blockbg)}
.sc-pfull{color:#c92a2a;background:#ffe3e3}
.sc-full td{background:#fff6f6}
.sc-btn{border:0;border-radius:9px;padding:8px 16px;font:inherit;font-weight:700;cursor:pointer;min-width:84px}
.sc-block{background:var(--block);color:#fff}
.sc-reopen{background:var(--open);color:#fff}
.sc-btn:disabled{opacity:.6;cursor:default}
.sc-empty{text-align:center;color:var(--muted);padding:26px}
.sc-foot{font-size:12px;color:var(--muted);margin-top:12px;line-height:1.55}
@media(max-width:560px){.sc-in{min-width:140px}.sc-loadwrap{max-width:120px}}
`;
